import { GzDialogWebComponent } from '../../../../../ui/gzDialog';
import {
  InProductTrialsOverlayWindowGroupId,
  InProductTrialsOverlayWindowType,
} from '../../types/InProductTrialsOverlayWindowType';
import { ButtonColors, ButtonTypes } from '../../../../../ui/gzButton/types';

export class LicenseChangePopup {
  private static computeSettings(
    translateFn: Function,
    groupId: InProductTrialsOverlayWindowGroupId,
    dialogBodyText: string,
    dialogTitle: string
  ) {
    const dialogFooterHtml =
      groupId === InProductTrialsOverlayWindowGroupId.ReadyToTry
        ? `
          <gz-button id='confirm' type='${ButtonTypes.FULL}' color='${ButtonColors.PRIMARY}' class='mr-2'>
           ${translateFn('Deploy on all endpoints')}
          </gz-button>
          <gz-button id='cancel' type='${ButtonTypes.OUTLINE}' color='${ButtonColors.SECONDARY}'>
            ${translateFn('Cancel')}
          </gz-button>
        `
        : `
          <gz-button id='confirm' type='${ButtonTypes.FULL}' color='${ButtonColors.PRIMARY}' class='mr-2'>
           ${translateFn('Remove from all endpoints')}
          </gz-button>
          <gz-button id='cancel' type='${ButtonTypes.OUTLINE}' color='${ButtonColors.SECONDARY}'>
            ${translateFn('Dismiss')}
          </gz-button>
        `;

    const dialogBodyHtml = `
          <p>
            ${dialogBodyText}
          </p>
          <p>
            ${
              groupId === InProductTrialsOverlayWindowGroupId.ReadyToTry
                ? translateFn(
                    'We recommend testing out the module on a smaller pool of endpoints before installing it on all your endpoints.'
                  )
                : translateFn('You can manually perform this action from the {bold}Network{/bold} page.', {
                    bold: '<strong>',
                    '/bold': '</strong>',
                  })
            }
          </p>
        `;

    return {
      dialogBodyHtml: dialogBodyHtml,
      dialogFooterHtml: dialogFooterHtml,
      dialogTitle: dialogTitle,
    };
  }

  private static getOverlayWindowTypeToPopupSettingsMapping(translateFn: Function) {
    return {
      // ready to try
      [InProductTrialsOverlayWindowType.OfficeSecurityPackReadyToTry]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.ReadyToTry,
        translateFn(
          "You are about to deploy the Content Control, Network Attack Defense and Device Control modules on all the company's endpoints using a reconfigure client task."
        ),
        translateFn("Add new modules to the company's endpoints?")
      ),
      [InProductTrialsOverlayWindowType.NextGenWithReconfigureReadyToTry]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.ReadyToTry,
        translateFn(
          "You are about to deploy the Content Control, Network Attack Defense and Device Control modules on all the company's endpoints using a reconfigure client task."
        ),
        translateFn("Add new modules to the company's endpoints?")
      ),
      [InProductTrialsOverlayWindowType.AtdIncidentsReadyToTry]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.ReadyToTry,
        translateFn(
          "You are about to deploy the EDR module on all your company's endpoints using a Reconfigure agent task."
        ),
        translateFn('Add EDR to all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.XdrFullReadyToTry]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.ReadyToTry,
        translateFn(
          "You are about to deploy the EDR module on all your company's endpoints using a Reconfigure agent task."
        ),
        translateFn('Add EDR to all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.PatchManagementNetworkReadyToTry]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.ReadyToTry,
        translateFn(
          "You are about to deploy the Patch Management module on all your company's endpoints using a Reconfigure agent task."
        ),
        translateFn('Add the Patch Management module to all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.VolumeEncryptionNetworkReadyToTry]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.ReadyToTry,
        translateFn(
          "You are about to deploy the Encryption module on all your company's endpoints using a Reconfigure agent task."
        ),
        translateFn('Add the Encryption module to all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.IntegrityMonitorReadyToTry]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.ReadyToTry,
        translateFn(
          "You are about to deploy the {bold}Integrity Monitoring{/bold} module on all your company's endpoints using a Reconfigure agent task.",
          {
            bold: '<strong>',
            '/bold': '</strong>',
          }
        ),
        translateFn('Add the Integrity Monitoring module to all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.NadAndEraReadyToTry]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.ReadyToTry,
        translateFn(
          "You are about to deploy the {bold}Network Monitor{/bold} and {bold}Risk Management{/bold} modules on all your company's endpoints using a Reconfigure agent task.",
          {
            bold: '<strong>',
            '/bold': '</strong>',
          }
        ),
        translateFn('Add the modules to all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.WebAccessAndDeviceControlReadyToTry]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.ReadyToTry,
        translateFn(
          "You are about to deploy the {bold}User Control{/bold} and {bold}Device Control{/bold} modules on all your company's endpoints using a Reconfigure agent task.",
          {
            bold: '<strong>',
            '/bold': '</strong>',
          }
        ),
        translateFn('Add the modules to all your endpoints?')
      ),

      // trial ended
      [InProductTrialsOverlayWindowType.OfficeSecurityPackTrialEnded]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.TrialEnded,
        translateFn(
          "You are about to remove the Content Control, Network Attack Defense and Device Control modules on all your company's endpoints using a Reconfigure agent task."
        ),
        translateFn('Remove the modules from all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.NextGenWithReconfigureTrialEnded]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.TrialEnded,
        translateFn(
          "You are about to remove the Content Control, Network Attack Defense and Device Control modules on all your company's endpoints using a Reconfigure agent task."
        ),
        translateFn('Remove the modules from all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.AtdTrialEnded]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.TrialEnded,
        translateFn(
          "You are about to remove the EDR module on all your company's endpoints using a Reconfigure agent task."
        ),
        translateFn('Remove EDR from all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.XdrFullTrialEnded]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.TrialEnded,
        translateFn(
          "You are about to remove the EDR module on all your company's endpoints using a Reconfigure agent task."
        ),
        translateFn('Remove EDR from all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.PatchManagementTrialEnded]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.TrialEnded,
        translateFn(
          "You are about to remove the Patch Management module on all your company's endpoints using a Reconfigure agent task."
        ),
        translateFn('Remove Patch Management from all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.VolumeEncryptionTrialEnded]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.TrialEnded,
        translateFn(
          "You are about to remove the Encryption module on all your company's endpoints using a Reconfigure agent task."
        ),
        translateFn('Remove the Encryption module from all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.IntegrityMonitorTrialEnded]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.TrialEnded,
        translateFn(
          "You are about to remove the {bold}Integrity Monitoring{/bold} module on all your company's endpoints using a reconfigure client task.",
          {
            bold: '<strong>',
            '/bold': '</strong>',
          }
        ),
        translateFn('Remove Integrity Monitoring from all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.ContainerProtectionTrialEnded]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.TrialEnded,
        translateFn(
          translateFn(
            "You are about to remove the {bold}Container Protection{/bold} module on all your company's endpoints using a reconfigure client task.",
            {
              bold: '<strong>',
              '/bold': '</strong>',
            }
          )
        ),
        translateFn('Remove Container Protection from all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.NadAndEraTrialEnded]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.TrialEnded,
        translateFn(
          "You are about to remove the {bold}Network Monitor{/bold} and {bold}Risk Management{/bold} modules on all your company's endpoints using a reconfigure client task.",
          {
            bold: '<strong>',
            '/bold': '</strong>',
          }
        ),
        translateFn('Remove the modules from all your endpoints?')
      ),
      [InProductTrialsOverlayWindowType.WebAccessAndDeviceControlTrialEnded]: LicenseChangePopup.computeSettings(
        translateFn,
        InProductTrialsOverlayWindowGroupId.TrialEnded,
        translateFn(
          "You are about to remove the {bold}User Control{/bold} and {bold}Device Control{/bold} modules on all your company's endpoints using a reconfigure client task.",
          {
            bold: '<strong>',
            '/bold': '</strong>',
          }
        ),
        translateFn('Remove the modules from all your endpoints?')
      ),
    };
  }

  public static showLicenseChangePopup(
    translateFn: Function,
    windowType: InProductTrialsOverlayWindowType,
    autoCloseOnConfirm: boolean = true,
    cancelCallback?: null | EventListenerOrEventListenerObject,
    confirmCallback?: null | EventListenerOrEventListenerObject
  ): GzDialogWebComponent | undefined {
    const popupSettings = LicenseChangePopup.getOverlayWindowTypeToPopupSettingsMapping(translateFn)[windowType];

    if (!popupSettings) {
      return;
    }

    return GzDialogWebComponent.computeCustomDialog(
      popupSettings.dialogBodyHtml,
      popupSettings.dialogFooterHtml,
      popupSettings.dialogTitle,
      cancelCallback,
      confirmCallback,
      null,
      autoCloseOnConfirm
    );
  }
}
